// ═══════════════════════════════════════════════════════════════
// Shared UI components — pixel-perfect match to design prototypes
// ═══════════════════════════════════════════════════════════════

import type { ReactNode } from 'react';
import { C, STATUS_CONFIG, VERSION_STATUS_CONFIG } from './design';
import { DOC_COLORS } from '../config/documentStyles';
import type { TipoDocumental } from '../types';
import { TIPO_DOCUMENTAL_LABEL } from '../types';
import { useI18n } from '../i18n';

// ─── SVG ICON PATHS ───
export const ICON_PATHS = {
  doc: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.35-4.35" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  chevDown: <path d="m6 9 6 6 6-6" />,
  chevRight: <path d="m9 18 6-6-6-6" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  check: <path d="M20 6 9 17l-5-5" />,
  edit: (
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  ),
  eye: (
    <>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  arrowRight: <path d="M5 12h14M12 5l7 7-7 7" />,
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  link: (
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  ),
  download: <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />,
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </>
  ),
  bold: <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6zM6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />,
  italic: <path d="M19 4h-9M14 20H5M15 4 9 20" />,
  underline: (
    <>
      <path d="M6 3v7a6 6 0 0 0 12 0V3" />
      <line x1="4" y1="21" x2="20" y2="21" />
    </>
  ),
  listBul: <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />,
  listNum: (
    <path d="M10 6h11M10 12h11M10 18h11M3 5l2-1v6M4 11h2M3 17.5a1.5 1.5 0 0 1 3 0 1.5 1.5 0 0 1-3 0z" />
  ),
  table: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </>
  ),
  save: (
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2zM17 21v-8H7v8M7 3v5h8" />
  ),
  undo: <path d="M3 7v6h6M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6.69 3L3 13" />,
  redo: <path d="M21 7v6h-6M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6.69 3L21 13" />,
  workflow: (
    <>
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="18" r="3" />
      <path d="M6 9v3a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V9" />
    </>
  ),
  print: (
    <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z" />
  ),
  meta: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M9 3v18" />
    </>
  ),
  img: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </>
  ),
  code: <path d="m16 18 6-6-6-6M8 6l-6 6 6 6" />,
  quote: (
    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21zM15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 .001 0 1.003 1 1.003z" />
  ),
  hr: <path d="M3 12h18" />,
  filter: <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />,
  send: <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />,
  tool: (
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  ),
  chart: (
    <>
      <path d="M18 20V10M12 20V4M6 20v-6" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  inbox: (
    <>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  clip: (
    <path d="M15.172 7l-6.586 6.586a2 2 0 1 0 2.828 2.828l6.414-6.586a4 4 0 0 0-5.656-5.656l-6.415 6.585a6 6 0 1 0 8.486 8.486L20.5 13" />
  ),
  sparkle: <path d="M12 2l2.09 6.26L20.18 10l-6.09 1.74L12 18l-2.09-6.26L3.82 10l6.09-1.74z" />,
  menu: <path d="M3 12h18M3 6h18M3 18h18" />,
} as const;

// ─── ICON COMPONENT ───
export function Ic({ d, s = 18, c = 'currentColor' }: { d: ReactNode; s?: number; c?: string }) {
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke={c}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {d}
    </svg>
  );
}

// ─── STATUS BADGE ───
// Accepts both EstadoDocumento and EstadoVersion values
export function StatusBadge({ status, small }: { status: string; small?: boolean }) {
  const { t } = useI18n();
  const cfg =
    (STATUS_CONFIG as any)[status] ??
    (VERSION_STATUS_CONFIG as any)[status] ??
    STATUS_CONFIG.BORRADOR;
  // Resolve label via i18n: try status (EstadoDocumento) then versionStatus (EstadoVersion)
  const label =
    (t.status as Record<string, string>)[status] ??
    (t.versionStatus as Record<string, string>)[status] ??
    cfg.label;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: small ? '2px 8px' : '4px 12px',
        borderRadius: 20,
        fontSize: small ? 11 : 13,
        fontWeight: 500,
        color: cfg.color,
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.color}22`,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.color }} />
      {label}
    </span>
  );
}

// ─── DOC TYPE BADGE ───
const BADGE_GRADIENTS: Record<string, [string, string]> = {
  FC: ['#6366F1', '#818CF8'],
  FL: ['#8B5CF6', '#A78BFA'],
  IT: ['#0EA5E9', '#38BDF8'],
  MO: ['#F59E0B', '#FBBF24'],
  NC: ['#2F5496', '#60A5FA'],
  PR: ['#059669', '#12B76A'],
  PA: ['#EC4899', '#F472B6'],
  RE: ['#6B7280', '#9CA3AF'],
  HE: ['#14B8A6', '#2DD4BF'],
  PT: ['#A855F7', '#C084FC'],
  FR: ['#0891B2', '#22D3EE'],
  FP: ['#D97706', '#FBBF24'],
  FG: ['#1F3864', '#2F5496'],
};

export function DocTypeBadge({ type }: { type: TipoDocumental }) {
  const grad = BADGE_GRADIENTS[type];
  const label = TIPO_DOCUMENTAL_LABEL[type] ?? type;
  return (
    <span
      title={label}
      style={{
        display: 'inline-flex',
        padding: '3px 8px',
        borderRadius: 6,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.5,
        color: '#fff',
        background: grad ? `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` : C.azul1,
        cursor: 'help',
      }}
    >
      {type}
    </span>
  );
}

// ─── NOTIFICATION TOAST ───
export function Toast({ msg }: { msg: string }) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 2000,
        padding: '12px 20px',
        borderRadius: 10,
        backgroundColor: C.azul1,
        color: '#fff',
        fontSize: 13,
        fontWeight: 500,
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        animation: 'slideIn 0.3s ease',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <Ic d={ICON_PATHS.check} s={16} c="#fff" />
      {msg}
    </div>
  );
}

// ─── GLOBAL STYLES (injected once) ───
export function GlobalStyles() {
  return (
    <style>{`
      @keyframes slideIn{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
      @keyframes fadeIn{from{opacity:0;transform:translate(-50%,-100%) scale(0.95)}to{opacity:1;transform:translate(-50%,-100%) scale(1)}}
      @keyframes slideRight{from{transform:translateX(100%)}to{transform:translateX(0)}}
      *{box-sizing:border-box}
      ::-webkit-scrollbar{width:4px;height:4px}
      ::-webkit-scrollbar-track{background:transparent}
      ::-webkit-scrollbar-thumb{background:${C.border};border-radius:2px}
      ::-webkit-scrollbar-thumb:hover{background:#a0a0a0}
      select,input{outline:none}
      select:focus,input:focus{border-color:${C.azul2}!important;box-shadow:0 0 0 3px ${C.azul2}18}
      button:hover{opacity:0.92}
      button:active{transform:scale(0.97)}
      .sbtn{font-size:11px;padding:2px 8px;border:1px solid #e2e8f0;background:white;color:#7a7a9a;border-radius:4px;cursor:pointer;transition:all 0.15s;font-family:inherit}
      .sbtn:hover{border-color:${DOC_COLORS.h2};color:${DOC_COLORS.h2}}
    `}</style>
  );
}
